// GodView.jsx — GOD · Mesa de Deus (native, powered by The Council).
//
// "Ensaie o futuro": plante uma SEMENTE de realidade (cenário), o Conselho
// multi-agente simula o futuro, e você joga de Deus — injeta variáveis e vê a
// trajetória dobrar a cada round (cada intervenção re-roda o Council com o
// estado anterior + o evento).
//
// Reuses Apolo's existing multi-agent: runCouncil (POST /api/council/run, SSE)
// + COUNCIL_AGENTS roster + the shared AddLlmDropdown. No backend changes.
// Design mirrors CouncilView (apolo-* tokens, framer-motion, glass, gold glow).

import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Zap, Crown, Loader2, X, Sparkles, ChevronDown, AlertTriangle, Sprout } from 'lucide-react';
import { runCouncil } from '../../lib/councilClient.js';
import { CHAIRMAN_COLOR, CHAIRMAN_GLOW, COUNCIL_AGENTS, getAgent, agentFromRegistryModel } from '../../lib/councilAgents.js';
import { findModel } from '../../lib/modelRegistry.js';
import { useUnifiedModels } from '../../hooks/useUnifiedModels.js';
import AddLlmDropdown from '../shared/AddLlmDropdown.jsx';
import ZeusEmblem from './ZeusEmblem.jsx';

const DEFAULT_AGENTS = ['claude-opus-4-7', 'gpt-4o', 'deepseek-chat', 'gemini-2.0-pro'];
const DEFAULT_CHAIRMAN = 'gpt-4o';
const LS_SCENARIO = 'apolo:god:scenario';
const LS_CUSTOM = 'apolo:god:custom-agents';

// Divine palette — Apolo's cosmic blue (apolo-accent) + the gold Chairman glow.
// Blue = the heavenly ambient field, gold = the warm focal light.
const DIVINE_BLUE = '#3b82f6';
const DIVINE_BLUE_DEEP = '#2563eb';

const PLACEHOLDER = `🌍 Mundo: A Glyco (startup de saúde digital) disputa o mercado de diabetes tipo 2 com a MedX, líder no B2B, em 12 estados.
🎭 Atores: Glyco (desafiante), MedX (líder), ANVISA, médicos endocrinologistas.
⚔️ Tensão: Glyco quer entrar no B2C; MedX defende o B2B.
📏 Regras: orçamento de R$2 mi, prazo regulatório da ANVISA.
❓ Pergunta: como o mercado evolui nos próximos 12 meses?`;

const STAGES = [
  { n: '01', t: 'Semente da realidade', d: 'Você planta a semente: mundo, atores, a tensão central e a pergunta. É aqui que o cenário nasce — quanto mais rica a semente, mais fundo o futuro simulado.', c: CHAIRMAN_COLOR },
  { n: '02', t: 'O Conselho simula', d: 'Os atores (LLMs) projetam em paralelo como o futuro evolui. O Chairman consolida tudo na previsão/decisão do round.', c: '#ec4899' },
  { n: '03', t: 'Mesa de Deus', d: 'Injete uma variável ou evento. O Conselho re-simula sobre o estado anterior — cada injeção vira um round e você vê a trajetória dobrar.', c: '#a855f7' },
];

function loadScenario() {
  try { return window.localStorage.getItem(LS_SCENARIO) || ''; } catch { return ''; }
}
function loadCustom() {
  try {
    const raw = window.localStorage.getItem(LS_CUSTOM);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch { return []; }
}

function buildBaselineQuery(scenario) {
  return [
    'Você faz parte de um conselho que SIMULA o futuro de um cenário (visão "Mesa de Deus").',
    '',
    '[SEMENTE / CENÁRIO]',
    scenario.trim(),
    '',
    '[TAREFA] A partir da sua perspectiva, projete como esse cenário evolui: o que os atores fazem, que dinâmicas emergem, e qual a decisão/aposta mais provável. Seja concreto e cite os atores.',
  ].join('\n');
}
function buildInterventionQuery(scenario, prevSynthesis, event, roundN) {
  return [
    'Você está SIMULANDO o futuro de um cenário (Mesa de Deus). Estado atual da simulação:',
    '',
    '[SEMENTE / CENÁRIO]',
    scenario.trim(),
    '',
    '[ESTADO ATUAL — síntese do round anterior]',
    (prevSynthesis || '(sem síntese anterior)').slice(0, 1800),
    '',
    `[⚡ INTERVENÇÃO DE DEUS — round ${roundN}]`,
    event.trim(),
    '',
    '[TAREFA] Reajam a essa intervenção: como a trajetória do cenário muda AGORA? O que os atores fazem diferente? Qual a nova decisão/aposta? Seja concreto.',
  ].join('\n');
}

// ── Agent chip ──
function AgentChip({ agent, selected, onToggle, isChairman, onChairman }) {
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onToggle(agent.id)}
        aria-pressed={selected}
        aria-label={`${selected ? 'Remover' : 'Adicionar'} ${agent.name}`}
        className={
          'group inline-flex min-h-[36px] items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all duration-200 focus:outline-none lg:min-h-0 lg:px-2.5 lg:py-1 lg:text-[11px] ' +
          (selected
            ? 'bg-apolo-surface2 text-apolo-textPrimary shadow-sm'
            : 'border-apolo-borderSubtle bg-transparent text-apolo-textSecondary hover:bg-apolo-surface2/60')
        }
        style={selected ? { borderColor: agent.color, boxShadow: `0 0 0 1px ${agent.color}40, 0 0 12px ${agent.glow}` } : {}}
      >
        <span aria-hidden="true" className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-bold text-apolo-bg" style={{ backgroundColor: agent.color }}>
          {agent.short}
        </span>
        <span>{agent.name}</span>
      </button>
      {selected && (
        <button
          type="button"
          onClick={() => onChairman(agent.id)}
          aria-pressed={isChairman}
          title={isChairman ? 'Chairman' : 'Designar como Chairman'}
          aria-label={isChairman ? `${agent.name} é Chairman` : `Designar ${agent.name} como Chairman`}
          className={
            'inline-flex h-9 w-9 items-center justify-center rounded-full border transition lg:h-5 lg:w-5 ' +
            (isChairman ? 'border-transparent text-apolo-bg' : 'border-apolo-borderSubtle text-apolo-textTertiary hover:text-apolo-textPrimary')
          }
          style={isChairman ? { backgroundColor: CHAIRMAN_COLOR, boxShadow: `0 0 10px ${CHAIRMAN_GLOW}` } : {}}
        >
          <Crown size={12} className="lg:h-[9px] lg:w-[9px]" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

// ── Stage tag (tiny "Etapa N" chip that labels each section) ──
function StageTag({ n, label, color }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color }}>
      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[8px]" style={{ backgroundColor: `${color}22`, color, border: `1px solid ${color}55` }}>{n}</span>
      {label}
    </span>
  );
}

// ── XSS-safe HTML helper ──
const escapeHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// ── Round card ──
function RoundCard({ round, extraAgents, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const isBaseline = round.kind === 'baseline';
  const accent = isBaseline ? '#ec4899' : CHAIRMAN_COLOR;
  const agentIds = Object.keys(round.opinionsByAgent);
  const synthesisText = round.synthesis || (round.status === 'running' ? 'Simulando…' : '');
  const lines = synthesisText.split('\n').filter(Boolean);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 28 }}
      className="relative flex flex-col gap-3 rounded-xl border bg-apolo-surface1/90 p-4 backdrop-blur-sm"
      style={{ borderColor: `${accent}55` }}
    >
      <div className="absolute inset-x-0 top-0 h-[2px]" style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }} aria-hidden="true" />
      <header className="flex items-center gap-2">
        <span aria-hidden="true" className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold" style={{ backgroundColor: `${accent}20`, color: accent, border: `1px solid ${accent}55` }}>
          {isBaseline ? '0' : round.n}
        </span>
        <div className="flex min-w-0 flex-col leading-tight">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-apolo-textPrimary">
            {isBaseline ? 'Baseline · futuro sem intervenção' : (<><Zap size={11} style={{ color: accent }} aria-hidden="true" /> Intervenção {round.n}</>)}
          </span>
          {!isBaseline && round.event && <span className="truncate text-[11px] text-apolo-textTertiary">{round.event}</span>}
        </div>
        <span className="ml-auto">
          {round.status === 'running' ? (
            <Loader2 size={13} className="animate-spin text-apolo-textTertiary" aria-label="Simulando" />
          ) : round.status === 'error' ? (
            <AlertTriangle size={13} className="text-apolo-error" aria-hidden="true" />
          ) : (
            <Crown size={13} style={{ color: CHAIRMAN_COLOR }} aria-hidden="true" />
          )}
        </span>
      </header>

      {round.error && (
        <p className="rounded-md border border-apolo-error/30 bg-apolo-errorBg px-2 py-1 text-[11px] text-apolo-error">{round.error}</p>
      )}

      <div className="space-y-1.5 text-sm leading-relaxed text-apolo-textPrimary">
        {lines.length ? (
          lines.map((line, i) => (
            <p key={i} dangerouslySetInnerHTML={{ __html: escapeHtml(line).replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-apolo-textPrimary">$1</strong>') }} />
          ))
        ) : (
          <p className="text-apolo-textTertiary">{round.status === 'running' ? 'Simulando…' : '—'}</p>
        )}
      </div>

      {agentIds.length > 0 && (
        <div className="border-t border-apolo-borderSubtle pt-2">
          <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 text-[11px] text-apolo-textTertiary transition hover:text-apolo-textSecondary">
            <ChevronDown size={12} className={'transition ' + (open ? 'rotate-180' : '')} aria-hidden="true" />
            {open ? 'Ocultar' : 'Ver'} as {agentIds.length} perspectivas dos atores
          </button>
          {open && (
            <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
              {agentIds.map((id) => {
                const a = getAgent(id, extraAgents) || { name: id, color: '#888', short: '?', provider: '' };
                const err = round.agentErrors[id];
                return (
                  <div key={id} className="rounded-lg border border-apolo-borderSubtle bg-apolo-surface2/60 p-2.5">
                    <div className="mb-1 flex items-center gap-1.5">
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-bold text-apolo-bg" style={{ backgroundColor: a.color }} aria-hidden="true">{a.short}</span>
                      <span className="text-[11px] font-medium text-apolo-textSecondary">{a.name}</span>
                    </div>
                    <p className="whitespace-pre-wrap text-[11px] leading-relaxed text-apolo-textTertiary">{err ? `⚠️ ${err}` : (round.opinionsByAgent[id] || '…')}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

export default function GodView() {
  const reduce = useReducedMotion();
  const [scenario, setScenario] = useState(loadScenario);
  const [selectedIds, setSelectedIds] = useState(DEFAULT_AGENTS);
  const [chairmanId, setChairmanId] = useState(DEFAULT_CHAIRMAN);
  const [customAgentIds, setCustomAgentIds] = useState(loadCustom);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [rounds, setRounds] = useState([]);
  const [running, setRunning] = useState(false);
  const [event, setEvent] = useState('');
  const abortRef = useRef(null);

  useEffect(() => { try { window.localStorage.setItem(LS_SCENARIO, scenario); } catch { /* ignore */ } }, [scenario]);
  useEffect(() => { try { window.localStorage.setItem(LS_CUSTOM, JSON.stringify(customAgentIds)); } catch { /* ignore */ } }, [customAgentIds]);
  useEffect(() => () => abortRef.current?.(), []);

  // Roster = hardcoded council agents + any LLM the user added (from the registry/adapters).
  const extraAgents = useMemo(() => customAgentIds.map((id) => agentFromRegistryModel(id, findModel(id))), [customAgentIds]);
  const effectiveRoster = useMemo(() => [...COUNCIL_AGENTS, ...extraAgents], [extraAgents]);

  const { allModels } = useUnifiedModels({ kind: 'text' });
  const availableToAdd = useMemo(() => {
    const rosterIds = new Set(effectiveRoster.map((a) => a.id));
    return allModels.filter((m) => !rosterIds.has(m.id));
  }, [effectiveRoster, allModels]);
  const groupedAvailable = useMemo(() => {
    const q = addSearch.trim().toLowerCase();
    const list = q ? availableToAdd.filter((m) => m.id.toLowerCase().includes(q) || (m.provider || '').toLowerCase().includes(q)) : availableToAdd;
    const groups = new Map();
    for (const m of list) {
      const key = m.provider || 'Outros';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(m);
    }
    return Array.from(groups.entries()).map(([provider, models]) => ({ provider, models }));
  }, [availableToAdd, addSearch]);

  const chairman = useMemo(() => (chairmanId ? getAgent(chairmanId, extraAgents) : null), [chairmanId, extraAgents]);
  const canRun = scenario.trim().length > 0 && selectedIds.length >= 2 && selectedIds.length <= 6 && !!chairmanId && !running;
  const canIntervene = event.trim().length > 0 && !running && rounds.length > 0;

  function toggleAgent(id) {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 6) return prev;
      return [...prev, id];
    });
    if (chairmanId === id && selectedIds.includes(id)) setChairmanId('');
  }
  function setChairman(id) { setChairmanId((prev) => (prev === id ? '' : id)); }
  function addCustomAgent(id) {
    if (!id) return;
    setCustomAgentIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setSelectedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setShowAddMenu(false);
  }
  function removeCustomAgent(id) {
    setCustomAgentIds((prev) => prev.filter((x) => x !== id));
    setSelectedIds((prev) => prev.filter((x) => x !== id));
    if (chairmanId === id) setChairmanId('');
  }

  function runRound(eventText) {
    const isBaseline = rounds.length === 0;
    const roundN = rounds.length;
    const prevSynthesis = rounds.length ? rounds[rounds.length - 1].synthesis : '';
    const query = isBaseline ? buildBaselineQuery(scenario) : buildInterventionQuery(scenario, prevSynthesis, eventText, roundN);

    setRounds((prev) => [...prev, { n: roundN, kind: isBaseline ? 'baseline' : 'intervention', event: eventText || '', opinionsByAgent: {}, agentErrors: {}, synthesis: '', status: 'running', error: '' }]);
    setRunning(true);

    const idx = roundN;
    const patch = (p) => setRounds((prev) => prev.map((r, i) => (i === idx ? { ...r, ...(typeof p === 'function' ? p(r) : p) } : r)));

    let run;
    try {
      run = runCouncil(
        { query, agents: selectedIds, chairman: chairmanId, enableReview: false },
        {
          onChunk: ({ agent_id, phase: p, chunk }) => {
            if (p === 1) patch((r) => ({ opinionsByAgent: { ...r.opinionsByAgent, [agent_id]: (r.opinionsByAgent[agent_id] || '') + chunk } }));
            else if (p === 3) patch((r) => ({ synthesis: r.synthesis + chunk }));
          },
          onPhaseComplete: ({ phase: p, result }) => { if (p === 3 && result) patch({ synthesis: result.response || '' }); },
          onError: ({ code, agent_id, message }) => {
            if (agent_id) patch((r) => ({ agentErrors: { ...r.agentErrors, [agent_id]: message || code } }));
            else patch({ error: `${code}: ${message || ''}` });
          },
          onDone: ({ status }) => { patch({ status: status === 'error' ? 'error' : 'done' }); setRunning(false); },
        },
      );
      abortRef.current = run.abort;
    } catch (e) {
      patch({ status: 'error', error: e.message });
      setRunning(false);
    }
  }

  function startSimulation() { if (canRun) runRound(null); }
  function injectIntervention() { if (!canIntervene) return; runRound(event.trim()); setEvent(''); }
  function cancel() {
    abortRef.current?.();
    setRunning(false);
    setRounds((prev) => prev.map((r, i) => (i === prev.length - 1 && r.status === 'running' ? { ...r, status: 'done' } : r)));
  }
  function reset() { abortRef.current?.(); setRounds([]); setRunning(false); setEvent(''); }

  const started = rounds.length > 0;

  return (
    <main id="tabpanel-god" role="tabpanel" aria-labelledby="tab-god" className="relative flex h-full flex-1 flex-col overflow-y-auto bg-apolo-bg">
      {/* ── Divine backdrop: blue heaven + gold light + god-rays (reduced-motion safe) ── */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        {/* faint celestial grid */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(${DIVINE_BLUE}0f 1px, transparent 1px), linear-gradient(90deg, ${DIVINE_BLUE}0f 1px, transparent 1px)`,
            backgroundSize: '46px 46px',
            maskImage: 'radial-gradient(ellipse 85% 58% at 50% 0%, #000 22%, transparent 78%)',
            WebkitMaskImage: 'radial-gradient(ellipse 85% 58% at 50% 0%, #000 22%, transparent 78%)',
          }}
        />
        {/* god-rays descending from above */}
        <div
          className="absolute -top-10 left-1/2 h-[24rem] w-[30rem] -translate-x-1/2"
          style={{
            background: `conic-gradient(from 180deg at 50% 0%, transparent 0deg, ${DIVINE_BLUE}1a 14deg, ${CHAIRMAN_COLOR}1f 28deg, ${DIVINE_BLUE}1a 42deg, transparent 56deg)`,
            maskImage: 'linear-gradient(#000, transparent)',
            WebkitMaskImage: 'linear-gradient(#000, transparent)',
          }}
        />
        {/* blue heaven dome */}
        <motion.div
          className="absolute -top-44 left-1/2 h-[30rem] w-[60rem] -translate-x-1/2 rounded-full blur-[130px]"
          style={{ background: `radial-gradient(circle, ${DIVINE_BLUE}3a, transparent 70%)` }}
          animate={reduce ? undefined : { opacity: [0.55, 0.85, 0.55] }}
          transition={reduce ? undefined : { duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* warm gold core */}
        <motion.div
          className="absolute -top-24 left-1/2 h-72 w-[34rem] -translate-x-1/2 rounded-full blur-[120px]"
          style={{ background: `radial-gradient(circle, ${CHAIRMAN_COLOR}30, transparent 70%)` }}
          animate={reduce ? undefined : { opacity: [0.4, 0.66, 0.4] }}
          transition={reduce ? undefined : { duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* deep blue settling at the bottom */}
        <div className="absolute bottom-[-7rem] right-[-4rem] h-72 w-96 rounded-full blur-[130px]" style={{ background: `radial-gradient(circle, ${DIVINE_BLUE_DEEP}22, transparent 70%)` }} />
      </div>

      {/* HEADER */}
      <header className="relative z-10 border-b border-apolo-borderSubtle px-4 py-4 lg:px-6 lg:py-5">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-1">
          <h1 className="flex items-center gap-2.5 text-xl font-semibold tracking-tight text-apolo-textPrimary lg:text-2xl">
            <motion.span
              aria-hidden="true"
              className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full lg:h-10 lg:w-10"
              style={{ background: `radial-gradient(circle, ${CHAIRMAN_COLOR}30 0%, ${DIVINE_BLUE}1c 55%, transparent 80%)`, boxShadow: [`0 0 28px ${DIVINE_BLUE}5c`, `0 0 12px ${CHAIRMAN_COLOR}8c`, `inset 0 0 0 1px ${CHAIRMAN_COLOR}3a`].join(', ') }}
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 280, damping: 22 }}
            >
              <ZeusEmblem size={28} className="relative z-10 lg:h-[32px] lg:w-[32px]" style={{ filter: `drop-shadow(0 0 5px ${CHAIRMAN_COLOR}b3) drop-shadow(0 0 9px ${DIVINE_BLUE}66)` }} />
            </motion.span>
            Mesa de Deus
          </h1>
          <p className="max-w-2xl text-xs text-apolo-textTertiary">
            Plante uma <strong className="font-medium text-apolo-textSecondary">semente de realidade</strong>, deixe o Conselho multi-agente simular o futuro e jogue de Deus: injete variáveis e veja a trajetória dobrar a cada round.
          </p>
        </div>
      </header>

      <div className="relative z-10 mx-auto flex w-full max-w-5xl flex-col gap-5 px-3 py-4 lg:gap-7 lg:px-6 lg:py-8">
        {/* ── Como funciona (explica cada etapa) — só no idle ── */}
        {!started && (
          <section aria-label="Como funciona" className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {STAGES.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: reduce ? 0 : i * 0.08, type: 'spring', stiffness: 300, damping: 26 }}
                className="flex flex-col gap-2 rounded-xl border border-apolo-borderSubtle bg-apolo-surface1/70 p-4 backdrop-blur-sm"
              >
                <span aria-hidden="true" className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold" style={{ backgroundColor: `${s.c}18`, color: s.c, border: `1px solid ${s.c}40` }}>{s.n}</span>
                <h3 className="flex items-center gap-1.5 text-xs font-semibold text-apolo-textPrimary">
                  {s.n === '01' && <Sprout size={12} style={{ color: s.c }} aria-hidden="true" />}
                  {s.n === '03' && <Zap size={12} style={{ color: s.c }} aria-hidden="true" />}
                  {s.t}
                </h3>
                <p className="text-[11px] leading-relaxed text-apolo-textTertiary">{s.d}</p>
              </motion.div>
            ))}
          </section>
        )}

        {/* ── ETAPA 1: SEMENTE / CENÁRIO ── */}
        <section className="flex flex-col gap-4 rounded-2xl border border-apolo-borderSubtle bg-apolo-surface1/85 p-3 backdrop-blur-sm lg:p-5">
          <div className="flex items-center justify-between">
            <StageTag n="1" label="Semente da realidade" color={CHAIRMAN_COLOR} />
            {started && (
              <button type="button" onClick={reset} disabled={running} className="inline-flex items-center gap-1.5 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 py-1.5 text-[11px] font-medium text-apolo-textSecondary transition hover:text-apolo-textPrimary disabled:opacity-40">
                <Sparkles size={12} aria-hidden="true" /> Nova semente
              </button>
            )}
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="flex items-center gap-1.5 text-xs font-medium text-apolo-textSecondary">
              <Sprout size={12} aria-hidden="true" />
              Cenário a simular
            </span>
            <span className="text-[11px] text-apolo-textTertiary">
              Esta é a <strong className="font-medium text-apolo-textSecondary">criação da semente</strong>: descreva o mundo, os atores, a tensão central e a pergunta. Quanto mais rica, mais profundo o futuro simulado.
            </span>
            <textarea
              value={scenario}
              onChange={(e) => setScenario(e.target.value)}
              rows={started ? 2 : 6}
              placeholder={PLACEHOLDER}
              disabled={started}
              className="mt-1 resize-y rounded-md border border-apolo-border bg-apolo-surface2 px-3 py-2 text-sm leading-relaxed text-apolo-textPrimary placeholder:text-apolo-textTertiary/70 focus:border-apolo-accent focus:outline-none disabled:opacity-70"
            />
          </label>

          {!started && (
            <>
              <div className="flex flex-col gap-2">
                <StageTag n="2" label="Atores do conselho" color="#ec4899" />
                <span className="text-[11px] text-apolo-textTertiary">
                  Cada LLM interpreta os atores e projeta o futuro · {selectedIds.length} selecionados · clique na <span className="inline-flex items-center"><Crown size={10} className="mx-0.5" style={{ color: CHAIRMAN_COLOR }} aria-hidden="true" /></span> pra escolher o Chairman (consolida a decisão).
                </span>
                <div className="flex flex-wrap items-center gap-1.5">
                  {effectiveRoster.map((a) => {
                    const isCustom = !COUNCIL_AGENTS.find((c) => c.id === a.id);
                    return (
                      <div key={a.id} className="relative">
                        <AgentChip agent={a} selected={selectedIds.includes(a.id)} onToggle={toggleAgent} isChairman={chairmanId === a.id} onChairman={setChairman} />
                        {isCustom && (
                          <button
                            type="button"
                            onClick={() => removeCustomAgent(a.id)}
                            aria-label={`Remover ${a.name}`}
                            title="Remover (custom)"
                            className="absolute -right-1 -top-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-apolo-borderSubtle bg-apolo-surface1 text-apolo-textTertiary transition hover:border-apolo-error hover:text-apolo-error"
                          >
                            <X size={9} aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  <AddLlmDropdown
                    open={showAddMenu}
                    onOpenChange={setShowAddMenu}
                    disabled={running || availableToAdd.length === 0}
                    count={availableToAdd.length}
                    groupedAvailable={groupedAvailable}
                    search={addSearch}
                    onSearchChange={setAddSearch}
                    onPick={addCustomAgent}
                    label="Adicionar ator (LLM)"
                  />
                </div>
              </div>

              <div className="flex flex-col items-stretch gap-2 pt-1 lg:flex-row lg:items-center lg:justify-between">
                <p className="text-[11px] text-apolo-textTertiary">
                  {chairman ? (<>Chairman: <strong className="font-semibold" style={{ color: chairman.color }}>{chairman.name}</strong></>) : 'Nenhum Chairman — clique na coroa de um ator.'}
                </p>
                <button
                  type="button"
                  onClick={startSimulation}
                  disabled={!canRun}
                  className="inline-flex h-11 items-center justify-center gap-1.5 rounded-md px-4 text-sm font-semibold transition focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-40 lg:h-auto lg:py-2 lg:text-xs"
                  style={{ backgroundColor: canRun ? CHAIRMAN_COLOR : 'transparent', color: canRun ? 'var(--apolo-bg)' : undefined, border: canRun ? 'none' : '1px solid var(--apolo-border)', boxShadow: canRun ? `0 0 18px ${CHAIRMAN_GLOW}, 0 0 30px ${DIVINE_BLUE}33` : 'none' }}
                >
                  <Sprout size={13} aria-hidden="true" />
                  Plantar &amp; simular futuro
                </button>
              </div>
            </>
          )}

          {started && (
            <span className="text-[11px] text-apolo-textTertiary">
              {selectedIds.length} atores · Chairman {chairman?.name || '—'} · {rounds.length} round{rounds.length === 1 ? '' : 's'} simulado{rounds.length === 1 ? '' : 's'}
            </span>
          )}
        </section>

        {/* ── TIMELINE: cada round é um futuro ── */}
        {started && (
          <section className="flex flex-col gap-3" aria-label="Linha do tempo da simulação">
            <div className="flex items-center gap-3">
              <StageTag n="2→3" label="Trajetória simulada" color="#a855f7" />
              <span className="text-[11px] text-apolo-textTertiary">cada card é um futuro · o baseline é o ponto de partida, cada intervenção dobra a curva</span>
            </div>
            <AnimatePresence>
              {rounds.map((r, i) => (
                <RoundCard key={r.n} round={r} extraAgents={extraAgents} defaultOpen={i === rounds.length - 1 && r.kind === 'baseline'} />
              ))}
            </AnimatePresence>
          </section>
        )}

        {/* ── ETAPA 3: MESA DE DEUS (intervenção) ── */}
        {started && (
          <section
            aria-label="Intervenção de Deus"
            className="sticky bottom-3 flex flex-col gap-2 rounded-2xl border bg-apolo-surface1/95 p-3 backdrop-blur-md"
            style={{ borderColor: `${DIVINE_BLUE}55`, boxShadow: `0 0 22px ${DIVINE_BLUE}33, 0 0 14px ${CHAIRMAN_COLOR}26, inset 0 0 0 1px ${CHAIRMAN_COLOR}22` }}
          >
            <div className="flex items-center justify-between">
              <StageTag n="3" label="Mesa de Deus" color={CHAIRMAN_COLOR} />
              <span className="hidden text-[10px] text-apolo-textTertiary sm:inline">injete uma variável → o Conselho re-simula sobre o estado atual</span>
            </div>
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
              <input
                type="text"
                value={event}
                onChange={(e) => setEvent(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && canIntervene) injectIntervention(); }}
                placeholder="Ex.: MedX corta preços 30% e lança no B2C — o que muda?"
                disabled={running}
                className="flex-1 rounded-md border border-apolo-border bg-apolo-surface2 px-3 py-2 text-sm text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none disabled:opacity-60"
              />
              {running ? (
                <button type="button" onClick={cancel} className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 text-xs font-medium text-apolo-textSecondary transition hover:text-apolo-error">
                  <X size={12} aria-hidden="true" /> Cancelar
                </button>
              ) : (
                <motion.button
                  type="button"
                  onClick={injectIntervention}
                  disabled={!canIntervene}
                  whileTap={reduce || !canIntervene ? undefined : { scale: 0.96 }}
                  className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-md px-4 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-40"
                  style={{ backgroundColor: canIntervene ? CHAIRMAN_COLOR : 'transparent', color: canIntervene ? 'var(--apolo-bg)' : undefined, border: canIntervene ? 'none' : '1px solid var(--apolo-border)', boxShadow: canIntervene ? `0 0 16px ${CHAIRMAN_GLOW}, 0 0 24px ${DIVINE_BLUE}30` : 'none' }}
                >
                  <Zap size={12} aria-hidden="true" /> Injetar variável
                </motion.button>
              )}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
